import {Component} from "@/event/calendar/dom/Component.ts";
import {CalendarView, CalendarViewName, savedSelectedDate, saveView} from "@/event/calendar/CalendarModel.ts";
import {watch} from "vue";
import {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";
import {HEADER_HEIGHT} from "@/event/calendar/Constants.ts";
import {ViewHeaderComponent, WRAP_AT} from "@/event/calendar/all/ViewHeaderComponent.ts";
import {FrameComponent} from "@/event/calendar/dom/FrameComponent.ts";
import {Rect} from "@/event/calendar/Shapes.ts";
import {CalendarComponent} from "@/event/calendar/all/CalendarComponent.ts";
import {CustomEvent} from "@/event/calendar/dom/CustomEvent.ts";
import {WeeksComponent} from "@/event/calendar/weeks/WeeksComponent.ts";
import {DaysComponent} from "@/event/calendar/days/DaysComponent.ts";
import {mondayDate} from "@t/util/DateUtil.ts";

/**
 * This component if calendar's root component which handle basic layout and view switching
 */
export class ViewComponent extends Component {
	private view: CalendarView;
	private header: ViewHeaderComponent;
	private body: Component;

	constructor(view: CalendarView) {
		super();
		this.view = view;
		// adding children
		this.header = new ViewHeaderComponent(view);
		this.body = this.createViewBodyInstance();
		this.append(this.body)
			.append(this.header);
	}

	createViewBodyInstance():Component {
		let body:CalendarComponent;
		switch (this.view.current) {
			case "days":
				body = new DaysComponent(this.view);
				break;
			case "weeks":
				body = new WeeksComponent(this.view);
				break;
		}
		return new FrameComponent("--cal-border").withBorderWidth(1, 5).append(body);
	}

	public switchView(viewName: CalendarViewName, day?:Date) {
		// Remove old view
		this.body.remove();
		const lastSavedSelectedDate = savedSelectedDate(this.view);	// may be stored by scrolling operation
		if (lastSavedSelectedDate) this.view.selected = lastSavedSelectedDate;
		this.view.current = viewName;
		this.prepend(this.body = this.createViewBodyInstance());
		if (day && viewName === "days") {
			if (this.view.snapMonday && this.view.days >= 5) {
				const dow = day.getDay();
				if (!(dow === 0 && this.view.days <= 6 || dow === 6 && this.view.days === 5)) {
					// Shift only if the target date will be visible after monday-shift
					day = mondayDate(day);
				}
			}
			this.calBody().moveToDate(day, false);
		}
		saveView(this.view);
		this.dirty(true);
	}

	calBody():CalendarComponent {
		const b = this.body.componentOfClass(CalendarComponent);
		if (b === undefined) throw new Error("Undefined calendar body in ViewComponent");
		return b;
	}


	placeChildren(dc: DrawingContext, contentBox: Rect) {
		// Header will be taller at small width
		const headerHeight = contentBox.width < WRAP_AT ? 2*HEADER_HEIGHT : HEADER_HEIGHT;
		this.header.x = contentBox.x;
		this.header.y = contentBox.y;
		this.header.width = contentBox.width;
		this.header.height = headerHeight;

		this.body.x = contentBox.x;
		this.body.y = contentBox.y + headerHeight;
		this.body.width = contentBox.width;
		this.body.height = contentBox.height - headerHeight;
	}


	layoutSelf(dc: DrawingContext, contentBox:Rect) {
	}


	onEvent(e: CustomEvent): boolean {
		if (e.name === "action" && e.id) {
			switch (e.id) {
				case "today":
					this.calBody().moveToToday(!!e.shift);
					return false;
				case "prev":
					this.calBody().moveToPrevious(!!e.shift);
					return false;
				case "next":
					this.calBody().moveToNext(!!e.shift);
					return false;
				case "zoomIn":
					this.calBody().zoomIn(!!e.shift);
					return false;
				case "zoomOut":
					this.calBody().zoomOut(!!e.shift);
					return false;
				case "viewDays":
					if (typeof e.columns === "number") this.view.days = e.columns;
					this.switchView("days", e.day);
					return false;
				case "viewWeeks":
					this.switchView("weeks", e.day);
					return false;
			}
		}
		return true;
	}
}